import { zodResolver } from "@hookform/resolvers/zod";
import {
  SkyCheckbox,
  SkyDialog,
  SkyDrawer,
  SkyDropdown,
  SkyInput,
  SkyRadioGroup,
  SkySwitch,
  SkyText,
  SkyTextarea,
  type CheckboxState,
} from "@skypointcloud/skypoint-ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import {
  MAX_RULE_EMAILS,
  MAX_SCOPED_RULE_TARGETS,
  NOTIFY_OUTCOMES,
  SCOPED_RULE_NAME_MAX,
  SCOPED_RULES_STALE_MESSAGE,
  type NotifyOutcome,
  type ScopedRule,
  type ScopedRulesView,
  type WatchTargetType,
} from "@shared/types/notifications";
import { OPEN_RECIPIENT_POLICY, type RecipientPolicy } from "@shared/types/notificationDomains";
import { useSaveScopedRules } from "@/hooks/use-alert-routing";
import { useDataflows } from "@/hooks/use-dataflows";
import { useSchedules } from "@/hooks/use-schedules";
import { ApiClientError } from "@/lib/api";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { emailLines, emailsProblem, externalLine } from "@/lib/notifications/rulesDraft";
import { SCOPED_OUTCOME_LABELS, TARGET_WORDS, ruleBodyFrom } from "@/lib/notifications/scopedRules";

/**
 * Add or edit one rule for chosen connections or schedules (alert routing, design "UI"): Name,
 * Applies to (Connections or Schedules), the list, When, the addresses and the Teams switch, and
 * On. A drawer keeps the list of rules in view. Saving sends the WHOLE set (the PUT is a full
 * replace guarded by the view's version), with this rule added, changed or, from Delete, left
 * out. A scoped rule never reaches L1, so there is no L1 switch here.
 *
 * react-hook-form with a zod resolver (client AGENTS.md); the limits are the server's own
 * (`shared/types/notifications.ts`).
 */

type ViewRule = ScopedRulesView["rules"][number];

/**
 * #1111: the address box refuses what the tenant's Allowed notification domains refuses: an
 * address outside it that this rule does not already hold (`held`, kept as the server keeps it).
 */
function formSchemaFor(policy: RecipientPolicy, held: readonly string[] = []) {
  return z
    .object({
      name: z
        .string()
        .trim()
        .min(1, "Name this rule.")
        .max(SCOPED_RULE_NAME_MAX, `At most ${SCOPED_RULE_NAME_MAX} characters.`),
      targetType: z.enum(["dataflow", "schedule"]),
      targetIds: z
        .array(z.string())
        .max(MAX_SCOPED_RULE_TARGETS, `At most ${MAX_SCOPED_RULE_TARGETS}.`),
      outcomes: z.array(z.enum(NOTIFY_OUTCOMES)).min(1, "Choose when this rule sends."),
      emailsText: z.string(),
      teams: z.boolean(),
      enabled: z.boolean(),
    })
    .superRefine((values, ctx) => {
      if (values.enabled && values.targetIds.length === 0) {
        ctx.addIssue({
          code: "custom",
          path: ["targetIds"],
          message: TARGET_WORDS[values.targetType].choose,
        });
      }
      const problem = emailsProblem(values.emailsText, policy, held);
      if (problem) ctx.addIssue({ code: "custom", path: ["emailsText"], message: problem });
    });
}
const formSchema = formSchemaFor(OPEN_RECIPIENT_POLICY);
type FormValues = z.infer<typeof formSchema>;

function valuesFrom(rule: ViewRule | null): FormValues {
  return {
    name: rule?.name ?? "",
    targetType: rule?.targetType ?? "dataflow",
    targetIds: rule ? rule.targets.map((target) => target.id) : [],
    outcomes: rule ? [...rule.outcomes] : ["failure"],
    emailsText: rule ? rule.emails.join("\n") : "",
    teams: rule?.teams ?? false,
    enabled: rule?.enabled ?? true,
  };
}

const ORDERED = NOTIFY_OUTCOMES;

export function ScopedRuleDrawer({
  view,
  rule,
  onClose,
}: {
  view: ScopedRulesView;
  /** Null adds a rule. */
  rule: ViewRule | null;
  onClose: () => void;
}) {
  const notice = useNotice();
  const failure = useInlineError();
  const save = useSaveScopedRules();
  const dataflows = useDataflows();
  const schedules = useSchedules();
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [nameClash, setNameClash] = useState<string | null>(null);
  const isEdit = rule !== null;

  const { control, handleSubmit, setValue } = useForm<FormValues>({
    resolver: zodResolver(formSchemaFor(view.recipientPolicy, rule?.emails ?? [])),
    defaultValues: valuesFrom(rule),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const targetType = useWatch({ control, name: "targetType" });

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      bodyRef.current
        ?.querySelector<HTMLInputElement>('[data-testid="scoped-rule-name"] input')
        ?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  /** Each option by current name; a saved target no longer listed stays, named as gone. */
  const options = useMemo(() => {
    const listed =
      targetType === "dataflow"
        ? (dataflows.data ?? []).map((row) => ({
            value: row.id,
            text: row.displayName,
          }))
        : (schedules.data ?? []).map((row) => ({ value: row.id, text: row.name }));
    const known = new Set(listed.map((option) => option.value));
    const gone =
      rule && rule.targetType === targetType
        ? rule.targets
            .filter((target) => !known.has(target.id))
            .map((target) => ({
              value: target.id,
              text:
                target.name ??
                (targetType === "dataflow" ? "Deleted connection" : "Deleted schedule"),
            }))
        : [];
    return [...listed, ...gone];
  }, [targetType, dataflows.data, schedules.data, rule]);

  async function send(rules: ScopedRule[], done: string): Promise<void> {
    failure.clear();
    try {
      await save.mutateAsync({
        instanceId: view.instanceId,
        body: { rules, expectedVersion: view.version },
      });
      onClose();
      notice.success(done);
    } catch (error) {
      if (error instanceof ApiClientError && error.code === "STALE_UPDATE") {
        failure.show(error, SCOPED_RULES_STALE_MESSAGE);
        return;
      }
      failure.show(error, "The rule could not be saved.");
    }
  }

  const submit = handleSubmit(async (values) => {
    const name = values.name.trim();
    const clash = view.rules.some(
      (other) => other.id !== rule?.id && other.name.trim().toLowerCase() === name.toLowerCase(),
    );
    if (clash) {
      setNameClash("Another rule already has this name.");
      return;
    }
    setNameClash(null);
    const next: ScopedRule = {
      ...(rule ? { id: rule.id } : {}),
      name,
      targetType: values.targetType,
      targetIds: values.targetIds,
      outcomes: ORDERED.filter((outcome) => values.outcomes.includes(outcome)),
      enabled: values.enabled,
      emails: emailLines(values.emailsText),
      teams: values.teams,
    };
    const others = view.rules.filter((other) => other.id !== rule?.id).map(ruleBodyFrom);
    await send(
      isEdit
        ? view.rules.map((other) => (other.id === rule.id ? next : ruleBodyFrom(other)))
        : [...others, next],
      `Rule saved: ${name}.`,
    );
  });

  const doDelete = async () => {
    if (!rule) return;
    setConfirmDelete(false);
    await send(
      view.rules.filter((other) => other.id !== rule.id).map(ruleBodyFrom),
      `Rule deleted: ${rule.name}.`,
    );
  };

  return (
    <>
      <SkyDrawer
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        position="end"
        size="medium"
        testId="scoped-rule-drawer"
        headerTitle={
          isEdit ? "Edit rule for chosen connections" : "Add a rule for chosen connections"
        }
        subtitle="Adds recipients for the connections or schedules you choose. The rules for every connection still apply."
        footerButtons={[
          ...(isEdit
            ? [
                {
                  label: "Delete",
                  appearance: "subtle" as const,
                  onClick: () => setConfirmDelete(true),
                  disabled: save.isPending,
                },
              ]
            : []),
          {
            label: "Cancel",
            appearance: "subtle" as const,
            onClick: onClose,
            disabled: save.isPending,
          },
          {
            label: "Save",
            appearance: "primary" as const,
            onClick: () => void submit(),
            loading: save.isPending,
            disabled: save.isPending,
          },
        ]}
      >
        <div ref={bodyRef}>
          <form
            className="flex flex-col gap-4"
            data-testid="scoped-rule-form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <SkyInput
                  label="Name"
                  required
                  value={field.value}
                  onChange={(event) => {
                    setNameClash(null);
                    field.onChange(event.target.value);
                  }}
                  onBlur={field.onBlur}
                  hint={`Up to ${SCOPED_RULE_NAME_MAX} characters. Emails sent by this rule name it.`}
                  validationState={fieldState.error || nameClash ? "error" : "none"}
                  validationMessage={fieldState.error?.message ?? nameClash ?? undefined}
                  testId="scoped-rule-name"
                />
              )}
            />

            <Controller
              control={control}
              name="targetType"
              render={({ field }) => (
                <SkyRadioGroup
                  label="Applies to"
                  layout="horizontal"
                  name="scoped-rule-type"
                  testId="scoped-rule-type"
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value as WatchTargetType);
                    // A list of connections means nothing once it applies to schedules.
                    setValue("targetIds", [], { shouldValidate: false });
                  }}
                  options={[
                    { value: "dataflow", label: "Connections" },
                    { value: "schedule", label: "Schedules" },
                  ]}
                />
              )}
            />

            <Controller
              control={control}
              name="targetIds"
              render={({ field, fieldState }) => (
                <SkyDropdown
                  key={targetType}
                  label={TARGET_WORDS[targetType].list}
                  multiselect
                  required
                  options={options}
                  selectedOptions={field.value}
                  placeholder={TARGET_WORDS[targetType].placeholder}
                  onOptionSelect={(selected) => field.onChange(selected)}
                  validationState={fieldState.error ? "error" : "none"}
                  validationMessage={fieldState.error?.message}
                  testId="scoped-rule-targets"
                />
              )}
            />

            <Controller
              control={control}
              name="outcomes"
              render={({ field, fieldState }) => (
                <div>
                  <SkyCheckbox
                    label="When"
                    layout="vertical"
                    options={ORDERED.map((outcome) => ({
                      id: outcome,
                      label: SCOPED_OUTCOME_LABELS[outcome],
                    }))}
                    value={field.value}
                    onChange={(states: CheckboxState[] | boolean) => {
                      if (typeof states === "boolean") return;
                      field.onChange(
                        states.filter((s) => s.checked).map((s) => s.id as NotifyOutcome),
                      );
                    }}
                    testId="scoped-rule-outcomes"
                  />
                  <SkyText as="p" size={200} color="muted" className="mt-1">
                    A connection is judged by its own result in the run, a schedule by the
                    run&apos;s.
                  </SkyText>
                  {fieldState.error ? (
                    <SkyText size={200} color="danger" role="alert" block>
                      {fieldState.error.message}
                    </SkyText>
                  ) : null}
                </div>
              )}
            />

            <Controller
              control={control}
              name="emailsText"
              render={({ field, fieldState }) => (
                <>
                  <SkyTextarea
                    label="Email"
                    value={field.value}
                    rows={Math.min(Math.max(emailLines(field.value).length + 1, 2), 6)}
                    hint={`One address per line, up to ${MAX_RULE_EMAILS}`}
                    validationState={fieldState.error ? "error" : "none"}
                    validationMessage={fieldState.error?.message}
                    testId="scoped-rule-emails"
                    onChange={(event) => field.onChange(event.target.value)}
                  />
                  {externalLine(emailLines(field.value), view.recipientPolicy) ? (
                    <SkyText size={200} color="muted" block testId="scoped-rule-external">
                      {externalLine(emailLines(field.value), view.recipientPolicy)}
                    </SkyText>
                  ) : null}
                </>
              )}
            />

            <Controller
              control={control}
              name="teams"
              render={({ field }) => (
                <SkySwitch
                  label="Post to this instance's Teams channel"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  testId="scoped-rule-teams"
                />
              )}
            />

            <Controller
              control={control}
              name="enabled"
              render={({ field }) => (
                <SkySwitch
                  label="On"
                  description="Off keeps the rule and sends nothing."
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  testId="scoped-rule-enabled"
                />
              )}
            />

            <InlineErrorBar
              error={failure.error}
              title="The rule could not be saved"
              testId="scoped-rule-failure"
            />
          </form>
        </div>
      </SkyDrawer>

      {confirmDelete && rule ? (
        <SkyDialog
          open
          onOpenChange={(open) => {
            if (!open) setConfirmDelete(false);
          }}
          title={`Delete ${rule.name}?`}
          description="Its recipients stop getting its alerts. The rules for every connection are not changed."
          size="small"
          testId="scoped-rule-delete-dialog"
          primaryAction={{
            label: "Delete rule",
            destructive: true,
            onClick: doDelete,
            loading: save.isPending,
          }}
          secondaryAction={{ label: "Cancel", onClick: () => setConfirmDelete(false) }}
        />
      ) : null}
    </>
  );
}
